import { GROK_WORKER_URL } from '../../../js/config.js';
import { storage } from '../../../js/utils/storage.js';
import { storageManager } from '../../../core/storage/index.js';
import { HangoutState } from '../state/HangoutState.js';
import { getCurrentVoiceLanguage } from './voice-settings.js';
import { ALL_VOICE_LANGUAGE_CODES } from '../../utils/languageData.js';
import { fetchCache } from '../../utils/fetchCache.js';
import { showError } from '../../utils/UnifiedNotificationSystem.js';
import { getSettingsModalTranslationText } from './modals/settings-dom-utils.js';

const OPENROUTER_KEY_VOICE_ERROR_CODES = new Set([
    'openrouter_key_invalid',
    'openrouter_key_credits',
    'openrouter_key_forbidden',
    'openrouter_key_rate_limited'
]);

const OPENROUTER_KEY_VOICE_ERROR_FALLBACKS = {
    openrouter_key_invalid: 'Your OpenRouter API key is invalid or expired. Update it in hangout settings.',
    openrouter_key_credits: 'Your OpenRouter account is out of credits. Add credits or clear the key in hangout settings.',
    openrouter_key_forbidden: 'OpenRouter rejected this request with your key. Check key permissions in hangout settings.',
    openrouter_key_rate_limited: 'OpenRouter rate-limited your key. Wait a moment and try again.'
};

const openRouterKeyVoiceError = (data) => {
    const code = data?.code;
    if (!OPENROUTER_KEY_VOICE_ERROR_CODES.has(code)) return null;
    const error = new Error(getSettingsModalTranslationText(code, OPENROUTER_KEY_VOICE_ERROR_FALLBACKS[code]));
    error.code = code;
    return error;
};

export const presentOpenRouterVoiceKeyError = (error) => {
    if (!OPENROUTER_KEY_VOICE_ERROR_CODES.has(error?.code)) return false;
    showError(error.message, { duration: 8000 });
    return true;
};

export const getAllCharacterIds = async () => {
    const hangoutState = HangoutState.getInstance();
    const currentSession = hangoutState.getCurrentSession();

    if (currentSession?.allCharacters && currentSession.allCharacters.length > 0) {
        return currentSession.allCharacters.map(c => String(c.id));
    }

    const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
    const activeCharacters = hangoutConfig?.allCharacters || [];

    if (activeCharacters.length > 0) {
        return activeCharacters.map(c => String(c.id));
    }

    const selectedChar = hangoutState.getSelectedCharacter();
    if (selectedChar?.id) {
        return [String(selectedChar.id)];
    }
    return [];
};

export const isGreetingMessage = (characterId) => {
    const hangoutState = HangoutState.getInstance();
    const messages = hangoutState.getMessages();

    if (!messages || messages.length === 0) return true;

    const characterMessages = messages.filter(msg =>
        msg.role === 'assistant' && String(msg.characterId) === String(characterId)
    );

    return characterMessages.length <= 1;
};

/**
 * Prepare the request body for voice generation
 * @param {string} characterId - Character ID
 * @param {string} messageText - Message text
 * @param {string|null} _messageId - Deprecated message ID (ignored)
 * @param {string|null} emotion - Emotion (optional)
 * @returns {Promise<Object>} Request body
 */
export const prepareVoiceRequestBody = async (characterId, messageText, _messageId = null, emotion = null) => {
    const voiceLanguage = getCurrentVoiceLanguage(characterId);
    const voiceIdPreferences = storage.get('characterVoiceIds') || {};
    const voiceProviderPreferences = storage.get('characterVoiceProviders') || {};
    const preferredVoiceId = voiceIdPreferences[characterId];
    const preferredVoiceProvider = voiceProviderPreferences[characterId] || null;
    const displayLanguage = storage.getLanguage();
    const hangoutState = HangoutState.getInstance();
    const conversationId = hangoutState.getConversationId();
    const currentSession = hangoutState.getCurrentSession();
    const scenarioId = currentSession?.scenarioId;
    const characterIds = await getAllCharacterIds();
    if (!characterIds.includes(String(characterId))) {
        characterIds.push(String(characterId));
    }
    const isGreeting = isGreetingMessage(characterId);
    const characters = hangoutState.getCharacters() || [];
    const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
    const charactersWithOutfits = characterIds.map((charId) => {
        const sessionCharacter = characters.find(c => String(c.id) === String(charId));
        const configCharacter = hangoutConfig?.allCharacters?.find(c => String(c.id) === String(charId));
        return {
            id: parseInt(charId),
            outfitId: parseInt(configCharacter?.outfitId ?? sessionCharacter?.outfitId ?? 0),
            gender: sessionCharacter?.gender ?? configCharacter?.gender
        };
    });

    const requestBody = {
        speaker: parseInt(characterId),
        characters: charactersWithOutfits,
        text: messageText,
        language: displayLanguage,
        voice_id: preferredVoiceId,
        voice_provider: preferredVoiceProvider,
        voiceLanguage: voiceLanguage,
        scenarioId: scenarioId,
        isGreeting: isGreeting
    };
    if (emotion) {
        requestBody.emotion = emotion;
    }
    if (conversationId) {
        requestBody.conversationId = conversationId;
    }
    return requestBody;
};

const applyVoiceQuotaError = async (status, data = {}, fallbackLabel = 'Voice generation') => {
    const keyError = openRouterKeyVoiceError(data);
    if (keyError) throw keyError;

    const isQuotaStatus = status === 429
        || (status === 403 && ['quota_exceeded', 'buzz_blocked', 'paid_required'].includes(data?.code));

    if (isQuotaStatus) {
        const errorPayload = data?.code
            ? data
            : { code: 'quota_exceeded', error: data?.error || 'Quota exceeded' };
        const { handleQuotaError } = await import('../../utils/quotaErrorHandler.js');
        if (await handleQuotaError(errorPayload, 'voice')) {
            throw new Error('QUOTA_HANDLED');
        }
    }

    if (status === 429 || status === 403) {
        throw new Error('VOICE_QUOTA_HANDLED');
    }

    throw new Error(`${fallbackLabel} failed: ${status} - ${data.error || 'Unknown error'}`);
};

const handleVoiceErrorResponse = async (response, fallbackLabel = 'Voice generation') => {
    if (response.ok) return;

    let data = {};
    try {
        data = await response.json();
    } catch {
        data = {};
    }

    await applyVoiceQuotaError(response.status, data, fallbackLabel);
};

const prepareVoiceRequestHeaders = async (requestBody) => {
    const authToken = (await window.tokenManager?.getValidAccessToken?.() ?? '') || '';
    if (!authToken && !requestBody.isGreeting) {
        throw new Error('Authentication required');
    }
    if (!requestBody.voiceLanguage) {
        const voiceLanguage = storage.get('voiceLanguage') || 'en';
        if (ALL_VOICE_LANGUAGE_CODES.includes(voiceLanguage)) {
            requestBody.voiceLanguage = voiceLanguage;
        }
    }
    const headers = { 'Content-Type': 'application/json' };
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    return headers;
};

export const callVoiceGenerationAPI = async (requestBody) => {
    const useStreaming = storage.get('useVoiceStreaming') !== 'false';
    if (useStreaming) {
        return await callStreamingVoiceGenerationAPI(requestBody);
    }
    const headers = await prepareVoiceRequestHeaders(requestBody);
    try {
        return await fetchCache.fetchJSON(`${GROK_WORKER_URL}/generate-voice`, {
            method: 'POST',
            headers,
            body: JSON.stringify(requestBody)
        });
    } catch (error) {
        if (error.status) {
            await applyVoiceQuotaError(error.status, error.body || {}, 'Voice generation');
        }
        throw error;
    }
};

/**
 * Call the streaming voice generation API
 * @param {Object} requestBody - Request body
 * @param {Function|null} onProgress - Progress callback
 * @param {Function|null} onStreamStart - Stream start callback
 * @returns {Promise<Object>} Response data
 */
export const callStreamingVoiceGenerationAPI = async (requestBody, onProgress = null, onStreamStart = null) => {
    const headers = await prepareVoiceRequestHeaders(requestBody);
    const response = await fetch(`${GROK_WORKER_URL}/generate-voice-stream`, { method: 'POST', headers, body: JSON.stringify(requestBody) });
    if (!response.ok) {
        await handleVoiceErrorResponse(response, 'Streaming voice generation');
    }
    return new Promise((resolve, reject) => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let hasStartedStreaming = false;
        let finalResult = null;
        const processStream = async () => {
            try {
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) {
                        if (finalResult) {
                            resolve(finalResult);
                        } else {
                            reject(new Error('Stream ended without final result'));
                        }
                        break;
                    }
                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop();
                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            try {
                                const data = JSON.parse(line.slice(6));
                                switch (data.type) {
                                    case 'stream_start':
                                        hasStartedStreaming = true;
                                        if (onStreamStart) onStreamStart(data);
                                        break;
                                    case 'progress':
                                        if (onProgress) onProgress(data);
                                        break;
                                    case 'complete':
                                        finalResult = data;
                                        break;
                                    case 'error': {
                                        const keyError = openRouterKeyVoiceError(data);
                                        reject(keyError || new Error(data.error || 'Streaming error'));
                                        return;
                                    }
                                }
                            } catch (parseError) {
                                console.warn('Failed to parse streaming data:', parseError);
                            }
                        }
                    }
                }
            } catch (error) {
                reject(error);
            }
        };
        processStream();
    });
};
